import { Component, ViewChild, ChangeDetectorRef, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { PublicationService } from '../../../services/publication.service';
import { CustomFieldsService } from '../../../services/custom-fields.service';
import { AbstractDetailsComponent } from '../../../components/shared/abstract-details.component';
import { NgForm } from '@angular/forms';
import { SapiService } from '../../../services/sapi.service';
import { BbTableService } from '../../../services/bb-table.service';
import { Location } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { StatsReport, ReportMetric } from '../../../interfaces/report';
import { CustomField } from '../../../interfaces/publication';
import { TabItem } from 'app/components/shared/bb-page-header/bb-page-header.component';

export interface Dimension {
	label: string;
	value: string;
	properties: string[];
}

const MAX_ROWS = 50;

@Component({
	selector: 'report-details',
	templateUrl: './report-details.component.html',
	styleUrls: ['./report-details.component.scss']
})

export class ReportDetailsComponent extends AbstractDetailsComponent implements OnInit {

	@ViewChild('formRef', { static: true }) form: NgForm;

	// TODO: implement preview or remove from html
	preview: boolean;
	tabItems: TabItem[];
	reportRights: boolean;

	activeTab: string;
	entity: any;

	previewReports: any;
	previewReportRows: any[] = [];
	previewReportHeaders: any;
	selectedColumns: string[];

	statsMetrics: any[];

	customFields: CustomField[];
	customStringFields: string[];

	dimensions: any;
	metrics: any;

	firstChange = true;

	constructor(protected route: ActivatedRoute,
		protected location: Location,
		protected publicationService: PublicationService,
		protected sapiService: SapiService,
		protected customFieldsService: CustomFieldsService,
		protected bbTableService: BbTableService,
		protected changeDetectorRef: ChangeDetectorRef,
		protected translate: TranslateService) {

		super(route, location);
		this.authenticationService.hasPermission('reports').subscribe(reportRights => this.reportRights = reportRights);

		this.entityConfiguration = {
			name: 'report',
			parentPath: '/analytics/reports',
			basePath: '/analytics/reports/',
			entityTranslation: 'reports',
			defaults: {
				dimensions: [{ label: this.translate.instant('MEDIACLIP'), properties: ['id', 'title'], value: 'mediaclip' }],
				metrics: [{ label: this.translate.instant('VIEWS'), value: 'views' }],
				initialSelection: 'views',
				firstWeekDay: 'monday',
				dynamicDateRange: 'last-30-days',
				dateRangeType: 'dynamic',
				filters: []
			}
		};
		this.subscriptions = new Subscription();
		this.customStringFields = [];
		this.statsMetrics = null;

		this.previewReports = require('../../../resources/defaults/previewReport.json');
		this.previewReportHeaders = require('../../../resources/defaults/previewReportHeaders.json');

		this.metrics = [
			'views',
			'creativeStart',
			'mainContentStarted',
			'completed',
			'inits',
			'viewTime',
			'viewTimeAverage',
			'viewcount20percent',
			'viewcount40percent',
			'viewcount60percent',
			'viewcount80percent'
		];


		this.customFieldsService.mediaclipCustomFields.subscribe((customFields: CustomField[]) => {
			if (customFields) {
				this.customFields = customFields;
				this.customFields.map((customField: CustomField) => {
					for (let i = 0; i < this.previewReports.length; i++) {
						let value = 'Example value';
						const previewReport = this.previewReports[i];
						if (previewReport.hasOwnProperty('custom_' + customField.itype)) {
							value = previewReport['custom_' + customField.itype];
						}
						this.previewReports[i][customField.name] = value;
						if (i === 0) {
							this.previewReportHeaders[customField.name] = customField.label;
							this.customStringFields.push(customField.name);
						}
					}
				});
			}

			const fields = ['granularity', 'playout', 'domain', 'custom_stats', 'quartiles', 'device_type', 'region', 'country', 'os', 'browser'];
			fields.forEach((field: string) => this.previewReports[0][field] = '');

			this.metrics.forEach((field: string) => this.previewReports[field] = 0);
		});

		this.tabItems = [
			{
				title: this.translate.instant('CONTENTS'),
				routerLink: ['/analytics/reports', 'contents'],
				icon: 'list-ol',
			},
			{
				title: this.translate.instant('SCHEDULE'),
				routerLink: ['/analytics/reports', 'schedule'],
				icon: 'clock-o',
			}
		];
	}

	ngOnInit() {
		super.ngOnInit();
	}

	createStats() {
		this.statsMetrics = [];

		const viewMetrics: number[] = [];

		for (let i = 0; i <= MAX_ROWS; i++) {
			viewMetrics.push(this.getRandomNumber('views'));
			this.statsMetrics.push({});
		}

		this.metrics.forEach((metric: string) => {

			for (let i = 0; i <= MAX_ROWS; i++) {
				const statsRow: any = this.statsMetrics[i];

				if (metric === 'views') {
					statsRow.views = viewMetrics[i];
				} else if (metric === 'viewTime') {
					statsRow[metric] = this.getRandomNumber(metric, viewMetrics[i]);
				} else if (metric === 'viewTimeAverage') {
					let viewTime = null;
					if (statsRow.hasOwnProperty(metric)) {
						viewTime = statsRow[metric];
					} else {
						viewTime = this.getRandomNumber('viewTime', viewMetrics[i]);
					}

					statsRow[metric] = Math.round(viewTime / viewMetrics[i]);
				} else if (metric.startsWith('viewcount')) {
					statsRow[metric] = this.getRandomNumber(metric, viewMetrics[i]);
					viewMetrics[i] = statsRow[metric];
				} else if (metric === 'mainContentStarted') {
					const views = viewMetrics[i] / 2;
					statsRow[metric] = this.getRandomNumber(metric, views);
				} else {
					statsRow[metric] = this.getRandomNumber(metric, 0);
				}
			}

		});

		for (let i = 0; i <= MAX_ROWS; i++) {
			const statsRow: any = this.statsMetrics[i];
			statsRow.completed = this.getRandomNumber('completed', viewMetrics[i]);
		}
	}

	reportChanged(report: StatsReport) {
		if (report) {

			if (!this.statsMetrics) {
				this.createStats();
			}

			let reportRows: any[] = [];
			const selectedColumnsSorted: string[] = [];
			let skipInits = false;

			if (report.dimensions) {
				const mediaclipDimensions = report.dimensions.filter((dimension: Dimension) => dimension.value === 'mediaclip');

				if (mediaclipDimensions && mediaclipDimensions.length) {
					const selectedColumns = mediaclipDimensions[0].properties;

					for (let i = 0; i < this.previewReports.length; i++) {
						const reportRow = {};
						const mediaclip = this.previewReports[i];

						Object.keys(mediaclip).forEach((key: string) => {
							if (selectedColumns.indexOf(key) > -1) {
								reportRow[key] = mediaclip[key];
								if (i === 0) {
									selectedColumnsSorted.push(key);
								}
							} else if (key.startsWith('mediaclip__')) {
								const key2 = key.replace('mediaclip__', '');
								if (selectedColumns.indexOf(key2) > -1) {
									reportRow[key] = mediaclip[key];
									if (i === 0) {
										selectedColumnsSorted.push(key);
									}
								}
							}
						});
						reportRows.push(reportRow);
					}
				}


				const otherDimensions = report.dimensions.filter((dimension: Dimension) => dimension.value !== 'mediaclip');

				if (otherDimensions && otherDimensions.length) {
					skipInits = true;
					const selectedDimensionColumns: string[] = [];
					otherDimensions.map((dimension: Dimension) => {
						selectedDimensionColumns.push(dimension.value);
						selectedColumnsSorted.push(dimension.value);
					});

					const allDimensions: any = {};
					allDimensions.granularity = ['19-02-2020', '20-02-2020', '21-02-2020', '22-02-2020'];
					allDimensions.playout = ['default', 'video', 'custom', 'audio'];
					allDimensions.domain = ['www.alpha.com', 'www.beta.com/gamma', 'www.theta.com', 'www.deltaquadrant.com'];
					allDimensions.custom_stats = ['stats event 1', 'stats event 2', 'stats event 3', 'stats event 4'];
					allDimensions.quartiles = ['25', '50', '75', '100'];
					allDimensions.device_type = ['desktop', 'smartphone', 'tablet'];
					allDimensions.region = ['Utrecht', 'Noord-Holland', 'Zuid-Holland', 'Gelderland'];
					allDimensions.country = ['Netherlands', 'Sweden', 'Bahamas', 'Barbados'];
					allDimensions.os = ['OSX', 'Windows', 'Android', 'iOS'];
					allDimensions.browser = ['Chrome', 'Firefox', 'Microsoft Edge', 'Safari'];
					allDimensions.OTHER = ['Some custom value', 'Another custom value', 'Another value', 'Something custom'];

					selectedDimensionColumns.forEach((dimension: string) => {
						if (reportRows.length < MAX_ROWS) {
							reportRows = this.addDimensionToRows(reportRows, dimension, allDimensions[dimension] || allDimensions.OTHER);
						} else {
							const reportRowsLength = reportRows.length;
							for (let i = 0; i < reportRowsLength; i++) {
								const reportRow = reportRows[i];
								if (allDimensions[dimension]) {
									reportRow[dimension] = allDimensions[dimension][0];
								} else {
									reportRow[dimension] = allDimensions.OTHER[0];
								}
							}
						}
					});
				}
			} else {
				this.dimensions = [];
			}
			if (reportRows.length && report.metrics) {

				this.metrics.forEach((metric: string) => {
					const reportMetrics = report.metrics.filter((m: ReportMetric) => {
						const skipMetrics = ['inits', 'viewcount20percent', 'viewcount40percent', 'viewcount60percent', 'viewcount80percent'];
						if (skipInits) {
							if (skipMetrics.indexOf(m.value) === -1) {
								return m.value === metric;
							}
						} else {
							return m.value === metric;
						}
					});

					if (reportMetrics && reportMetrics.length) {
						selectedColumnsSorted.push(reportMetrics[0].value);

						const reportRowsLength: number = reportRows.length;

						for (let i = 0; i < reportRowsLength; i++) {
							const reportRow = reportRows[i];

							reportRow[metric] = this.statsMetrics[i][metric];
						}
					}
				});

				this.selectedColumns = selectedColumnsSorted;

			} else {
				this.metrics = [];
			}

			this.previewReportRows = reportRows;

			if (this.firstChange) {
				this.firstChange = false;
				this.changeDetectorRef.detectChanges();
			}

		}
	}

	getHeaderLabel(type: string) {
		if (type && type.length) {
			if (this.customStringFields.indexOf(type) > -1) {
				const customFields: CustomField[] = this.customFields.filter((customField: CustomField) => customField.name === type);
				if (customFields && customFields.length) {
					return customFields[0].label;
				}
			} else {
				return this.translate.instant(this.previewReportHeaders[type]);
			}
		}
		return '';
	}

	getHeaderClass(type: string) {
		if (type && type.length) {
			if (this.customStringFields.indexOf(type) > -1) {
				const customFields: CustomField[] = this.customFields.filter((customField: CustomField) => customField.name === type);
				if (customFields && customFields.length) {
					return 'custom_' + customFields[0].itype;
				}
			}
		}
		return type;
	}

	getRandomNumber(metric: string, lastNumber: number = 0) {
		let value = null;
		switch (metric) {
			case 'views': value = 900;
				break;
			case 'creativeStart': value = 1700;
				break;
			case 'inits': value = 20000;
				break;
			case 'viewTime': value = lastNumber * 80;
				break;
			default:
				break;
		}

		if (value) {
			value = Math.round((Math.random() + 0.3) * value);
		} else if (lastNumber > 0) {
			value = Math.round((Math.random()) * lastNumber);
		} else {
			value = 0;
		}

		return value;
	}

	addDimensionToRows(reportRows: any[], dimension: string, dimensionValues: string[]) {
		const rows: any[] = [];
		for (let i = 0; i < reportRows.length; i++) {
			dimensionValues.forEach((dimensionValue: string) => {
				if (rows.length < MAX_ROWS) {
					const row = { ...reportRows[i] };
					row[dimension] = dimensionValue;
					rows.push(row);
				}
			});
		}
		return rows;
	}
}
